import {
  ARCHIVED_CHAT_LIST_QUERY_KEY,
  CHAT_LIST_QUERY_KEY,
} from "@frontend-shared/constants";
import { EAnalyticsEvent } from "@frontend-shared/product-analytics";
import type { ICreateChatShareBody } from "@frontend-shared/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { chatApi } from "@/api/services";
import { useProductAnalytics } from "@/hooks/use-product-analytics";

import { chatShareQueryKey } from "./chat-share.constants";

export const useCreateChatShare = (chatUuid: string) => {
  const queryClient = useQueryClient();
  const { capture } = useProductAnalytics();

  return useMutation({
    mutationFn: (body: ICreateChatShareBody) =>
      chatApi.createChatShare(chatUuid, body),
    onSuccess: (data, body) => {
      // NOTE: NSAI-4616 — knowledge, assistants and meetings each had a share
      // event; chat did not, which left the most-used surface out of the
      // collaboration picture. `access_scope` is share configuration, not
      // content — recipients and the chat itself never travel.
      capture({
        event: EAnalyticsEvent.CHAT_SHARED,
        properties: { access_scope: body.accessScope },
      });

      queryClient.setQueryData(chatShareQueryKey(chatUuid), data);
      // NOTE: Refresh the chat lists so the sidebar's shared indicator
      // (hasActiveShare) updates without requiring a page reload.
      queryClient.invalidateQueries({ queryKey: [CHAT_LIST_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [ARCHIVED_CHAT_LIST_QUERY_KEY],
      });
    },
    // NOTE: ShareChatDialog shows its own error toast (incl. the rate-limited
    // case) — suppress the global mutation-cache toast so one failure doesn't
    // produce two toasts.
    meta: { suppressErrorToast: true },
  });
};
